<template>
  <div class="impact-analysis-page" data-testid="impact-analysis-view">
    <div class="mb-4">
      <h1 class="text-[22px] font-bold text-[var(--color-dark)] m-0 leading-tight">
        {{ $t('UserProjects.impactAnalysis.pageTitle') }}
      </h1>
      <p class="text-[13px] text-[var(--color-disabled)] mt-0.5 mb-0">
        {{ pageSubline }}
      </p>
    </div>

    <Message v-if="!selectedProjectId && userProjectContextReady" severity="info" :closable="false">
      {{ $t('UserProjects.context.required') }}
    </Message>

    <Message v-else-if="!isFullView && userProjectContextReady && selectedProjectId" severity="info" :closable="false">
      {{ $t('UserProjects.impactAnalysis.memberRestricted') }}
    </Message>

    <Message v-if="error" severity="error" :closable="false">
      {{ error }}
    </Message>

    <div v-if="loading && !data" class="text-sm text-[var(--color-disabled)] py-8">
      {{ $t('UserProjects.impactAnalysis.loading') }}
    </div>

    <template v-else-if="selectedProjectId && userProjectContextReady && isFullView">
      <div
        v-if="insufficientData"
        class="flex flex-col items-center justify-center text-center py-20"
        data-testid="impact-insufficient-data"
      >
        <i class="pi pi-chart-bar text-[3rem] mb-4 text-[#D0D0D0]" aria-hidden="true" />
        <p class="text-[15px] font-medium text-[var(--color-dark)] m-0">
          {{ $t('UserProjects.impactAnalysis.insufficient.title') }}
        </p>
        <p class="text-[14px] text-[var(--color-disabled)] mt-1 mb-0 max-w-md">
          {{ $t('UserProjects.impactAnalysis.insufficient.body') }}
        </p>
      </div>

      <template v-else-if="data">
        <div class="flex flex-wrap items-center gap-3 mt-2 mb-6">
          <span
            class="h-9 px-3 rounded-[10px] text-[13px] font-medium text-[var(--color-dark)] bg-white border border-[#E5E5E5] inline-flex items-center"
            data-testid="impact-project-label"
          >
            {{ selectedProject?.name || '—' }}
          </span>
          <span
            class="h-9 px-3 rounded-[10px] text-[13px] font-medium text-[var(--color-dark)] bg-white border border-[#E5E5E5] inline-flex items-center"
            data-testid="impact-period-label"
          >
            {{ periodLabel || '—' }}
          </span>
        </div>

        <ImpactChainStrip
          :pattern-detected="patternDetected"
          :sees-impuls="canSeeImpuls"
          :behavior="wirkkette?.behavior"
          :cause="wirkkette?.cause"
          :kpi="wirkkette?.kpi"
          :impuls-preview="wirkkette?.impulsPreview"
        />

        <div class="mt-6">
          <ImpactForecastBlock :forecast="forecast" />
        </div>

        <div class="mt-8 space-y-5">
          <ImpactStageSection
            :label="$t('UserProjects.impactAnalysis.stages.behaviorFull')"
            stripe-color="#3B82F6"
            :bg-color="patternDetected ? '#EFF6FF10' : '#FFFFFF'"
            test-id="impact-stage-behavior"
          >
            <ImpactVerhaltenDetail
              :tbi-bars="tbiBars"
              :tbi-current="tbiCurrent"
              :period-label="periodLabel"
              :dimensions="dimensions"
            />
          </ImpactStageSection>

          <ImpactStageSection
            :label="$t('UserProjects.impactAnalysis.stages.causeFull')"
            stripe-color="#EAB308"
            :bg-color="patternDetected ? '#FEFCE810' : '#FFFFFF'"
            test-id="impact-stage-cause"
          >
            <ImpactUrsacheDetail
              :distribution="causes.distribution"
              :total-breaks="causes.totalBreaks"
              :items="constraints.items"
              :summary="constraints.summary"
              :period-label="periodLabel"
            />
          </ImpactStageSection>

          <ImpactStageSection
            :label="$t('UserProjects.impactAnalysis.stages.kpiFull')"
            stripe-color="#E5E5E5"
            bg-color="#FFFFFF"
            test-id="impact-stage-kpi"
          >
            <ImpactKennzahlDetail
              :ppc-bars="ppcBars"
              :ppc-current="ppcCurrent"
              :target-percent="ppcTarget"
              :target-gap="wirkkette?.kpi?.targetGap ?? null"
              :milestones="milestones"
              :period-label="periodLabel"
              :interface-quality="interfaceQuality"
            />
          </ImpactStageSection>

          <ImpactStageSection
            v-if="canSeeImpuls"
            :label="$t('UserProjects.impactAnalysis.stages.impulsFull')"
            stripe-color="var(--color-orange)"
            :bg-color="patternDetected ? '#FDF0E610' : '#FFFFFF'"
            test-id="impact-stage-impuls"
          >
            <ImpactFuehrungsimpulsDetail :impuls="fuehrungsimpuls" />
          </ImpactStageSection>

          <ImpactStageSection
            :label="$t('UserProjects.impactAnalysis.stages.dataBasis')"
            stripe-color="#E5E5E5"
            bg-color="#FFFFFF"
            test-id="impact-stage-data"
          >
            <ImpactDataBasisPanel :data-basis="dataBasis" />
          </ImpactStageSection>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Message from 'primevue/message';
import ImpactChainStrip from '@/components/CustomerApp/UserProjects/ImpactAnalysis/ImpactChainStrip.vue';
import ImpactForecastBlock from '@/components/CustomerApp/UserProjects/ImpactAnalysis/ImpactForecastBlock.vue';
import ImpactStageSection from '@/components/CustomerApp/UserProjects/ImpactAnalysis/ImpactStageSection.vue';
import ImpactVerhaltenDetail from '@/components/CustomerApp/UserProjects/ImpactAnalysis/ImpactVerhaltenDetail.vue';
import ImpactUrsacheDetail from '@/components/CustomerApp/UserProjects/ImpactAnalysis/ImpactUrsacheDetail.vue';
import ImpactKennzahlDetail from '@/components/CustomerApp/UserProjects/ImpactAnalysis/ImpactKennzahlDetail.vue';
import ImpactFuehrungsimpulsDetail from '@/components/CustomerApp/UserProjects/ImpactAnalysis/ImpactFuehrungsimpulsDetail.vue';
import ImpactDataBasisPanel from '@/components/CustomerApp/UserProjects/ImpactAnalysis/ImpactDataBasisPanel.vue';

export default {
  name: 'UserProjectsImpactAnalysisView',
  components: {
    Message,
    ImpactChainStrip,
    ImpactForecastBlock,
    ImpactStageSection,
    ImpactVerhaltenDetail,
    ImpactUrsacheDetail,
    ImpactKennzahlDetail,
    ImpactFuehrungsimpulsDetail,
    ImpactDataBasisPanel,
  },
  computed: {
    ...mapGetters('pmProject', {
      selectedProjectId: 'selectedProjectId',
      selectedProject: 'selectedProject',
      userProjectContextReady: 'userProjectContextReady',
    }),
    ...mapGetters('pmImpactAnalysis', [
      'loading',
      'error',
      'data',
      'insufficientData',
      'patternDetected',
      'periodLabel',
      'wirkkette',
      'tbiCurrent',
      'tbiBars',
      'ppcCurrent',
      'ppcBars',
      'ppcTarget',
      'causes',
      'constraints',
      'milestones',
      'forecast',
      'fuehrungsimpuls',
      'interfaceQuality',
      'dimensions',
      'dataBasis',
      'canSeeImpuls',
      'isFullView',
    ]),
    pageSubline() {
      const projectName = this.selectedProject?.name || this.$t('UserProjects.context.empty');
      return this.periodLabel ? `${projectName} · ${this.periodLabel}` : projectName;
    },
  },
  watch: {
    userProjectContextReady: {
      immediate: true,
      handler(ready) {
        if (ready) this.loadPage();
      },
    },
    selectedProjectId() {
      if (this.userProjectContextReady) this.loadPage();
    },
  },
  methods: {
    ...mapActions('pmImpactAnalysis', ['fetchImpactAnalysis']),
    async loadPage() {
      if (!this.selectedProjectId || !this.isFullView) return;
      try {
        await this.fetchImpactAnalysis();
      } catch {
        // error stored in Vuex
      }
    },
  },
};
</script>

<style scoped>
.impact-analysis-page {
  padding: 0.25rem 0 2rem;
}
</style>
